import { useEffect, useState, useRef, useMemo } from 'react';
import { GoogleMap, DirectionsRenderer, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from './GoogleMapsContext';
// import { Imagesdata } from '../../common/commonimages.jsx';

function NPMapViewDirections(props) {
  const { isLoaded, loadError } = useGoogleMaps();

  const [directions, setDirections] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 49.8951, lng: -97.1384 }); // Winnipeg
  const [shouldCenter, setShouldCenter] = useState(true);

  // Current driver position
  const [markerPos, setMarkerPos] = useState(null);
  // Previous driver position
  const prevPosRef = useRef(null);

  const directionsServiceRef = useRef(null);
  const prevMapDataRef = useRef({ origins: [], dropoffs: [] });

  const mapOrigins = useMemo(
    () =>
      props.origins.map((o) => ({
        latitude: o.latitude,
        longitude: o.longitude,
        address: o.address,
      })),
    [props.origins]
  );

  const mapDropoffs = useMemo(
    () =>
      props.dropoffs.map((d) => ({
        latitude: d.latitude,
        longitude: d.longitude,
        address: d.address,
      })),
    [props.dropoffs]
  );

  useEffect(() => {
    if (!isLoaded) return;
    if (!directionsServiceRef.current) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }
  }, [isLoaded]);

  // If user changed the route's origins/dropoffs, recalc Directions
  useEffect(() => {
    if (!isLoaded || !(mapOrigins.length && mapDropoffs.length)) return;

    const prevMapData = prevMapDataRef.current;
    const originsChanged = checkChanged(prevMapData.origins, mapOrigins);
    const dropoffsChanged = checkChanged(prevMapData.dropoffs, mapDropoffs);

    if (!originsChanged && !dropoffsChanged) return;

    prevMapDataRef.current = { origins: mapOrigins, dropoffs: mapDropoffs };
    setDirections(null);

    const directionsService = directionsServiceRef.current;
    if (!directionsService) return;

    const waypoints = [...mapOrigins, ...mapDropoffs].map((point) => ({
      location: new window.google.maps.LatLng(point.latitude, point.longitude),
      stopover: true,
    }));

    const request = {
      origin: new window.google.maps.LatLng(
        mapOrigins[0].latitude,
        mapOrigins[0].longitude
      ),
      destination: new window.google.maps.LatLng(
        mapDropoffs[mapDropoffs.length - 1].latitude,
        mapDropoffs[mapDropoffs.length - 1].longitude
      ),
      waypoints,
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    };

    directionsService.route(request, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        setDirections(result);

        if (props.onWaypointsOptimized) {
          const optimizedWaypoints = result.routes[0].waypoint_order.map(
            (i) => waypoints[i]
          );
          props.onWaypointsOptimized(optimizedWaypoints);
        }
        if (props.onDistanceCalculated) {
          const totalDistance = result.routes[0].legs.reduce(
            (acc, leg) => acc + leg.distance.value,
            0
          );
          props.onDistanceCalculated(totalDistance / 1000);
        }

        if (shouldCenter) {
          setMapCenter({
            lat: mapOrigins[0].latitude,
            lng: mapOrigins[0].longitude,
          });
          setShouldCenter(false);
        }
      } else {
        console.error('Error fetching directions:', status);
      }
    });
  }, [
    isLoaded,
    mapOrigins,
    mapDropoffs,
    shouldCenter,
    props.onWaypointsOptimized,
    props.onDistanceCalculated,
  ]);

  // Animate the marker from old => new location
  useEffect(() => {
    if (!props.driverLocation) return;

    // If this is the first time, just set markerPos
    if (!prevPosRef.current) {
      prevPosRef.current = props.driverLocation;
      setMarkerPos(props.driverLocation);
      return;
    }

    animateMarker(prevPosRef.current, props.driverLocation, 1000); // 1 second
  }, [props.driverLocation]);

  // Animate from 'startPos' => 'endPos' over 'duration' ms
  function animateMarker(startPos, endPos, duration) {
    const startTime = performance.now();

    function frame(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // linear interpolation (you can add easing if you like)
      const currentLat = startPos.lat + (endPos.lat - startPos.lat) * progress;
      const currentLng = startPos.lng + (endPos.lng - startPos.lng) * progress;

      setMarkerPos({ lat: currentLat, lng: currentLng });

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        // done
        prevPosRef.current = endPos;
      }
    }
    requestAnimationFrame(frame);
  }

  // If origins or dropoffs change, allow the map to recenter
  useEffect(() => {
    setShouldCenter(true);
  }, [mapOrigins, mapDropoffs]);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading...</div>;

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '400px' }}
      center={mapCenter}
      zoom={13}
    >
      {directions && <DirectionsRenderer directions={directions} />}

      {/* Car Marker */}
      {/* {markerPos && (
        <Marker
          position={markerPos}
          icon={{
            url: Imagesdata('iconCar'),
            scaledSize: new window.google.maps.Size(32, 32),
          }}
          options={{ zIndex: 9999 }}
        />
      )} */}
    </GoogleMap>
  );
}

function checkChanged(oldArr, newArr) {
  if (oldArr.length !== newArr.length) return true;
  for (let i = 0; i < oldArr.length; i++) {
    if (
      oldArr[i].latitude !== newArr[i].latitude ||
      oldArr[i].longitude !== newArr[i].longitude
    ) {
      return true;
    }
  }
  return false;
}

export default NPMapViewDirections;
